import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { professionalsAPI, appointmentsAPI, reviewsAPI, chatAPI, chariowAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import ReviewsList from '@/components/ReviewsList';
import {
  ArrowLeft,
  Stethoscope,
  MapPin,
  Star,
  Clock,
  Video,
  Home,
  Calendar as CalendarIcon,
  CheckCircle,
  CreditCard,
  Loader2,
  MessageCircle,
  AlertCircle,
  ExternalLink,
  Smartphone,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addMinutes, parse, isAfter, isBefore, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';

// Chariow payment links mapping
const CHARIOW_LINKS = {
  3000: 'https://urvjccdj.mychariow.store/prd_f8mu6p3a',
  5000: 'https://urvjccdj.mychariow.store/prd_tm5xy3d5',
  10000: 'https://urvjccdj.mychariow.store/prd_zh8ll8sw',
  15000: 'https://urvjccdj.mychariow.store/prd_cb8048jl',
};

// Get the closest Chariow price tier
const getChariowLink = (price) => {
  const tiers = [3000, 5000, 10000, 15000];
  // Find the tier that matches or is just above the price
  const tier = tiers.find(t => t >= price) || tiers[tiers.length - 1];
  return { link: CHARIOW_LINKS[tier], tier };
};

const ProfessionalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [professional, setProfessional] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingStep, setBookingStep] = useState(0); // 0: view, 1: select date, 2: select time, 3: payment, 4: confirm
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedType, setSelectedType] = useState('consultation');
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [createdAppointment, setCreatedAppointment] = useState(null);
  const [startingChat, setStartingChat] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  // Chariow payment states
  const [paymentReference, setPaymentReference] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('pending'); // pending, checking, confirmed, failed
  const [pollingInterval, setPollingInterval] = useState(null);

  // Get current user from localStorage
  const user = JSON.parse(localStorage.getItem('dockhta_user') || '{}');

  useEffect(() => {
    loadProfessional();
  }, [id]);

  // Load available slots when date or type changes
  useEffect(() => {
    if (selectedDate && professional) {
      loadAvailableSlots();
    }
  }, [selectedDate, selectedType, professional]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Poll for payment status
  const startPaymentPolling = useCallback((reference) => {
    const interval = setInterval(async () => {
      try {
        const response = await chariowAPI.checkStatus(reference);
        const status = response.data.status;
        
        if (status === 'confirmed') {
          clearInterval(interval);
          setPollingInterval(null);
          setPaymentStatus('confirmed');
          setBookingSuccess(true);
          setCreatedAppointment({ id: response.data.appointment_id });
          toast.success('Paiement confirmé ! Votre téléconsultation est réservée.');
        } else if (status === 'payment_failed') {
          clearInterval(interval);
          setPollingInterval(null);
          setPaymentStatus('failed');
          toast.error('Le paiement a échoué. Veuillez réessayer.');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      }
    }, 5000); // Poll every 5 seconds
    
    setPollingInterval(interval);
  }, []);

  const loadProfessional = async () => {
    try {
      const response = await professionalsAPI.getDetail(id);
      setProfessional(response.data.professional);
      
      // Load reviews
      const reviewsRes = await reviewsAPI.getProfessionalReviews(id);
      setReviews(reviewsRes.data.reviews || []);
    } catch (error) {
      console.error('Error loading professional:', error);
      toast.error('Professionnel non trouvé');
      navigate('/search');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableSlots = async () => {
    if (!selectedDate || !professional) return;
    
    setLoadingSlots(true);
    try {
      // Get day name in English for slot lookup
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayName = dayNames[selectedDate.getDay()];
      
      // Get slots based on consultation type
      let slots = [];
      let duration = 30; // default
      
      if (selectedType === 'consultation') {
        slots = professional.consultation_slots?.[dayName] || [];
        duration = professional.consultation_duration || 30;
      } else if (selectedType === 'video_call') {
        slots = professional.video_slots?.[dayName] || [];
        duration = professional.video_duration || 20;
      }
      
      // Generate time slots from ranges
      const generatedSlots = generateTimeSlots(slots, duration);
      
      // Get already booked appointments for this day
      try {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const bookedRes = await appointmentsAPI.getBookedSlots(professional.user_id, dateStr);
        setBookedSlots(bookedRes.data.booked_slots || []);
      } catch (e) {
        console.log('Could not fetch booked slots:', e);
        setBookedSlots([]);
      }
      
      setAvailableSlots(generatedSlots);
    } catch (error) {
      console.error('Error loading slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  // Generate individual time slots from a range
  const generateTimeSlots = (ranges, durationMinutes) => {
    const slots = [];
    const now = new Date();
    
    ranges.forEach(range => {
      if (!range.start || !range.end) return;
      
      let current = parse(range.start, 'HH:mm', new Date());
      const end = parse(range.end, 'HH:mm', new Date());
      
      while (isBefore(current, end)) {
        const timeStr = format(current, 'HH:mm');
        
        // Skip past slots if booking for today
        if (selectedDate && isSameDay(selectedDate, now)) {
          const slotDateTime = new Date(selectedDate);
          const [hours, minutes] = timeStr.split(':').map(Number);
          slotDateTime.setHours(hours, minutes, 0, 0);
          
          if (isAfter(slotDateTime, now)) {
            slots.push(timeStr);
          }
        } else {
          slots.push(timeStr);
        }
        
        current = addMinutes(current, durationMinutes);
      }
    });
    
    return slots;
  };

  const isSlotBooked = (time) => {
    return bookedSlots.includes(time);
  };

  const getFilteredSlots = () => {
    return availableSlots.filter(slot => !isSlotBooked(slot));
  };

  const getPrice = () => {
    if (!professional) return 0;
    switch (selectedType) {
      case 'consultation': return professional.consultation_fee || 0;
      case 'video_call': return professional.video_fee || 0;
      case 'home_visit': return professional.home_visit_fee || 0;
      default: return 0;
    }
  };

  const handleBooking = async () => {
    setProcessing(true);
    try {
      // Create appointment (free booking - payment on-site)
      const appointmentData = {
        professional_id: id,
        appointment_type: selectedType,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time_slot: selectedTime,
        notes: notes,
        payment_method: 'on_site', // Payment on-site
        payment_status: 'pending', // Will be paid on-site
      };

      const appointmentRes = await appointmentsAPI.create(appointmentData);
      const appointmentId = appointmentRes.data.appointment_id;

      setCreatedAppointment({ id: appointmentId, ...appointmentData });
      setBookingSuccess(true);
      setBookingStep(4);
      toast.success('Rendez-vous confirmé!');
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la réservation');
    } finally {
      setProcessing(false);
    }
  };

  const handleStartChat = async () => {
    setStartingChat(true);
    try {
      // Use the professional's user_id, not the profile id
      const professionalUserId = professional?.user_id || professional?.user?.id;
      if (!professionalUserId) {
        toast.error('Impossible de démarrer la conversation');
        return;
      }
      const response = await chatAPI.createConversation(professionalUserId);
      const conversationId = response.data.conversation.id;
      navigate(`/chat/${conversationId}`);
    } catch (error) {
      console.error('Error starting chat:', error);
      toast.error('Erreur lors du démarrage de la conversation');
    } finally {
      setStartingChat(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  if (!professional) {
    return null;
  }

  return (
    <div className="section-padding py-6" data-testid="professional-detail-page">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>Retour</span>
      </button>

      {/* Profile Header */}
      <Card className="border-0 shadow-lg mb-6 overflow-hidden">
        <div className="h-24 bg-gradient-to-r from-red-500 to-red-600" />
        <CardContent className="relative pt-0 pb-6">
          <div className="flex flex-col md:flex-row md:items-end gap-4 -mt-12">
            <div className="w-24 h-24 rounded-2xl bg-white shadow-lg flex items-center justify-center border-4 border-white">
              <Stethoscope className="w-12 h-12 text-red-500" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Dr. {professional.user?.full_name}
              </h1>
              <p className="text-red-500 font-medium">{professional.specialty}</p>
              <div className="flex flex-wrap items-center gap-4 mt-2">
                {professional.rating > 0 && (
                  <div className="flex items-center gap-1 text-amber-500">
                    <Star className="w-4 h-4 fill-current" />
                    <span className="font-medium">{professional.rating.toFixed(1)}</span>
                    <span className="text-slate-400 text-sm">({professional.total_reviews} avis)</span>
                  </div>
                )}
                {professional.address && (
                  <div className="flex items-center gap-1 text-slate-500">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm">{professional.address}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {professional.description && (
            <p className="mt-6 text-slate-600">{professional.description}</p>
          )}
        </CardContent>
      </Card>

      {/* Services & Pricing */}
      <Card className="border-0 shadow-sm mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Services & Tarifs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {professional.services?.includes('consultation') && (
            <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <CalendarIcon className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Consultation sur place</p>
                  <p className="text-sm text-slate-500">En clinique</p>
                </div>
              </div>
              <span className="font-semibold text-slate-900">
                {professional.consultation_fee?.toLocaleString()} FCFA
              </span>
            </div>
          )}
          {professional.services?.includes('video_call') && (
            <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Video className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Téléconsultation</p>
                  <p className="text-sm text-slate-500">Appel vidéo</p>
                </div>
              </div>
              <span className="font-semibold text-slate-900">
                {professional.video_fee?.toLocaleString()} FCFA
              </span>
            </div>
          )}
          {professional.services?.includes('home_visit') && (
            <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Home className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Visite à domicile</p>
                  <p className="text-sm text-slate-500">Chez vous</p>
                </div>
              </div>
              <span className="font-semibold text-slate-900">
                {professional.home_visit_fee?.toLocaleString()} FCFA
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reviews Section */}
      <Card className="border-0 shadow-sm mb-6" data-testid="reviews-section">
        <CardContent className="p-6">
          <ReviewsList reviews={reviews} showTitle={true} />
        </CardContent>
      </Card>

      {/* Book Button */}
      <div className="flex gap-3">
        <Button
          onClick={handleStartChat}
          disabled={startingChat}
          variant="outline"
          className="h-14 px-6 rounded-full border-[#E53935] text-[#E53935] hover:bg-[#E53935]/10"
          data-testid="message-professional-button"
        >
          {startingChat ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <MessageCircle className="w-5 h-5" />
          )}
        </Button>
        <Button
          onClick={() => setBookingStep(1)}
          className="flex-1 h-14 bg-red-500 hover:bg-red-600 text-white text-lg font-semibold rounded-full shadow-lg"
          data-testid="book-appointment-button"
        >
          Prendre rendez-vous
        </Button>
      </div>

      {/* Booking Modal */}
      <Dialog open={bookingStep > 0} onOpenChange={(open) => !open && setBookingStep(0)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {bookingStep === 1 && (
            <>
              <DialogHeader>
                <DialogTitle>Choisir le type de consultation</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-4">
                {professional.services?.includes('consultation') && (
                  <button
                    onClick={() => { setSelectedType('consultation'); setBookingStep(2); }}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      selectedType === 'consultation' ? 'border-red-500 bg-red-50' : 'border-slate-200 hover:border-red-200'
                    }`}
                    data-testid="select-consultation"
                  >
                    <div className="flex items-center gap-3">
                      <CalendarIcon className="w-6 h-6 text-red-500" />
                      <div>
                        <p className="font-semibold">Consultation sur place</p>
                        <p className="text-sm text-slate-500">{professional.consultation_fee?.toLocaleString()} FCFA</p>
                      </div>
                    </div>
                  </button>
                )}
                {professional.services?.includes('video_call') && (
                  <button
                    onClick={() => { setSelectedType('video_call'); setBookingStep(2); }}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      selectedType === 'video_call' ? 'border-purple-500 bg-purple-50' : 'border-slate-200 hover:border-purple-200'
                    }`}
                    data-testid="select-video-call"
                  >
                    <div className="flex items-center gap-3">
                      <Video className="w-6 h-6 text-purple-600" />
                      <div>
                        <p className="font-semibold">Téléconsultation</p>
                        <p className="text-sm text-slate-500">{professional.video_fee?.toLocaleString()} FCFA</p>
                      </div>
                    </div>
                  </button>
                )}
                {professional.services?.includes('home_visit') && (
                  <button
                    onClick={() => { setSelectedType('home_visit'); setBookingStep(2); }}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      selectedType === 'home_visit' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-amber-200'
                    }`}
                    data-testid="select-home-visit"
                  >
                    <div className="flex items-center gap-3">
                      <Home className="w-6 h-6 text-amber-600" />
                      <div>
                        <p className="font-semibold">Visite à domicile</p>
                        <p className="text-sm text-slate-500">{professional.home_visit_fee?.toLocaleString()} FCFA</p>
                      </div>
                    </div>
                  </button>
                )}
              </div>
            </>
          )}

          {bookingStep === 2 && (
            <>
              <DialogHeader>
                <DialogTitle>Choisir la date et l&apos;heure</DialogTitle>
                <DialogDescription>
                  Sélectionnez une date puis un créneau horaire disponible
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    setSelectedDate(date);
                    setSelectedTime(''); // Reset time when date changes
                  }}
                  disabled={(date) => {
                    // Disable past dates
                    if (date < new Date().setHours(0, 0, 0, 0)) return true;
                    
                    // Get day name
                    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                    const dayName = dayNames[date.getDay()];
                    
                    // Check if professional has slots for this day
                    let slots = [];
                    if (selectedType === 'consultation') {
                      slots = professional?.consultation_slots?.[dayName] || [];
                    } else if (selectedType === 'video_call') {
                      slots = professional?.video_slots?.[dayName] || [];
                    }
                    
                    // Disable day if no slots configured
                    return slots.length === 0;
                  }}
                  locale={fr}
                  className="rounded-xl border mx-auto"
                />

                {selectedDate && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-slate-700 text-sm">
                        Créneaux le {format(selectedDate, 'EEEE dd MMMM', { locale: fr })}
                      </p>
                      <Badge variant="outline" className="text-xs">
                        <Clock className="w-3 h-3 mr-1" />
                        {selectedType === 'consultation' 
                          ? `${professional?.consultation_duration || 30} min`
                          : `${professional?.video_duration || 20} min`
                        }
                      </Badge>
                    </div>
                    
                    {loadingSlots ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="w-6 h-6 animate-spin text-red-500" />
                      </div>
                    ) : getFilteredSlots().length > 0 ? (
                      <div className="max-h-32 overflow-y-auto border border-slate-200 rounded-lg p-2">
                        <div className="grid grid-cols-4 gap-1.5">
                          {getFilteredSlots().map((time) => (
                            <button
                              key={time}
                              onClick={() => setSelectedTime(time)}
                              className={`py-1.5 px-2 rounded-md text-xs font-medium transition-all ${
                                selectedTime === time
                                  ? 'bg-red-500 text-white shadow-md'
                                  : 'bg-slate-100 text-slate-700 hover:bg-red-50 hover:text-red-600'
                              }`}
                              data-testid={`time-slot-${time}`}
                            >
                              {time}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4 bg-slate-50 rounded-xl">
                        <AlertCircle className="w-6 h-6 text-amber-500 mx-auto mb-1" />
                        <p className="text-slate-600 font-medium text-sm">Aucun créneau disponible</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {availableSlots.length > 0 
                            ? 'Tous les créneaux sont déjà réservés'
                            : 'Pas de disponibilité ce jour'
                          }
                        </p>
                      </div>
                    )}
                    
                    {bookedSlots.length > 0 && getFilteredSlots().length > 0 && (
                      <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        {getFilteredSlots().length} créneau(x) disponible(s)
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-4">
                  <Label htmlFor="notes" className="text-sm">Notes (optionnel)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Motif de consultation..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="mt-1 text-sm"
                    rows={2}
                  />
                </div>

                {/* Sticky button area */}
                <div className="sticky bottom-0 bg-white pt-4 pb-2 -mx-6 px-6 border-t border-slate-100 mt-4">
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setBookingStep(1)} className="flex-1 h-10">
                      Retour
                    </Button>
                    <Button
                      onClick={() => setBookingStep(3)}
                      disabled={!selectedDate || !selectedTime}
                      className="flex-1 bg-red-500 hover:bg-red-600 h-10"
                      data-testid="continue-to-payment"
                    >
                      Continuer
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}

          {bookingStep === 3 && (
            <>
              <DialogHeader>
                <DialogTitle>Confirmer votre rendez-vous</DialogTitle>
                <DialogDescription>
                  Vérifiez les détails et confirmez
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                {/* Summary */}
                <div className="p-4 bg-slate-50 rounded-xl space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Professionnel</span>
                    <span className="font-medium">Dr. {professional.user?.full_name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Service</span>
                    <span className="font-medium">
                      {selectedType === 'consultation' ? 'Consultation' : selectedType === 'video_call' ? 'Téléconsultation' : 'Domicile'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Date</span>
                    <span className="font-medium">{selectedDate && format(selectedDate, 'EEEE dd MMMM yyyy', { locale: fr })}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Heure</span>
                    <span className="font-medium">{selectedTime}</span>
                  </div>
                </div>

                {/* Payment Info - Different for consultation vs teleconsultation */}
                {selectedType === 'video_call' ? (
                  // Teleconsultation - Chariow Payment Required
                  <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <Smartphone className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-purple-900">Paiement Mobile Money requis</p>
                        <p className="text-xs text-purple-700">Payez avant la téléconsultation</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-purple-200">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-purple-800 font-medium">Tarif de la téléconsultation</span>
                        <span className="text-xl font-bold text-purple-700">{getPrice().toLocaleString()} FCFA</span>
                      </div>
                      <div className="bg-purple-100 rounded-lg p-3 text-xs text-purple-800">
                        <p className="font-medium mb-1">📱 Comment ça marche :</p>
                        <ol className="list-decimal list-inside space-y-1">
                          <li>Cliquez sur &quot;Payer avec Mobile Money&quot;</li>
                          <li>Payez via Orange Money, MTN MoMo ou Wave</li>
                          <li>Votre RDV sera confirmé après réception</li>
                          <li>Vous recevrez le lien de téléconsultation</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                ) : (
                  // In-person consultation - Payment on-site
                  <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-emerald-900">Paiement sur place</p>
                        <p className="text-xs text-emerald-700">Payez directement lors de votre consultation</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-emerald-200 flex items-center justify-between">
                      <span className="text-emerald-800 font-medium">Tarif de la consultation</span>
                      <span className="text-xl font-bold text-emerald-700">{getPrice().toLocaleString()} FCFA</span>
                    </div>
                  </div>
                )}

                {/* Notes if any */}
                {notes && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-600 font-medium mb-1">Motif de consultation :</p>
                    <p className="text-sm text-blue-800">{notes}</p>
                  </div>
                )}

                <div className="flex gap-3 mt-6">
                  <Button variant="outline" onClick={() => setBookingStep(2)} className="flex-1">
                    Retour
                  </Button>
                  {selectedType === 'video_call' ? (
                    // Teleconsultation - Create pending and redirect to Chariow
                    <Button
                      onClick={async () => {
                        setProcessing(true);
                        try {
                          const price = getPrice();
                          
                          // Create pending teleconsultation in backend with actual price
                          const response = await chariowAPI.createPending({
                            patient_id: user.id,
                            patient_email: user.email,
                            professional_id: id,
                            professional_name: professional.user?.full_name,
                            date: format(selectedDate, 'yyyy-MM-dd'),
                            time_slot: selectedTime,
                            notes: notes,
                            amount: price  // Send actual price from professional's profile
                          });
                          
                          const { reference, chariow_url } = response.data;
                          setPaymentReference(reference);
                          setPaymentStatus('pending');
                          
                          toast.info('Redirection vers Chariow en cours...');
                          
                          // Try to open in new tab, fallback to redirect
                          const newWindow = window.open(chariow_url, '_blank');
                          
                          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                            // Popup blocked - redirect in same window
                            toast.warning('Popup bloqué - redirection directe...');
                            setTimeout(() => {
                              window.location.href = chariow_url;
                            }, 1000);
                          } else {
                            // Show waiting step and start polling
                            setBookingStep(4);
                            setBookingSuccess(false);
                            startPaymentPolling(reference);
                          }
                          
                        } catch (error) {
                          console.error('Error creating pending teleconsultation:', error);
                          toast.error('Erreur lors de la création de la réservation');
                        } finally {
                          setProcessing(false);
                        }
                      }}
                      disabled={processing}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                      data-testid="pay-chariow-button"
                    >
                      {processing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ExternalLink className="w-4 h-4 mr-2" />
                      )}
                      Payer avec Mobile Money
                    </Button>
                  ) : (
                    // In-person consultation - Confirm directly
                    <Button
                      onClick={handleBooking}
                      disabled={processing}
                      className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                      data-testid="confirm-booking-button"
                    >
                      {processing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Confirmation...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Confirmer le rendez-vous
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}

          {bookingStep === 4 && (
            <>
              {bookingSuccess ? (
                // Rendez-vous confirmé (consultation sur place OU téléconsultation payée)
                <div className="text-center py-8">
                  <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-emerald-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">
                    {selectedType === 'video_call' ? 'Téléconsultation confirmée!' : 'Rendez-vous confirmé!'}
                  </h2>
                  <p className="text-slate-600 mb-6">
                    {selectedType === 'video_call' 
                      ? `Votre paiement a été reçu. Votre téléconsultation avec Dr. ${professional.user?.full_name} est confirmée.`
                      : `Votre rendez-vous avec Dr. ${professional.user?.full_name} est confirmé.`
                    }
                  </p>
                  <div className="p-4 bg-slate-50 rounded-xl text-left mb-6">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-500">Date</span>
                      <span className="font-medium">{selectedDate && format(selectedDate, 'dd MMMM yyyy', { locale: fr })}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-500">Heure</span>
                      <span className="font-medium">{selectedTime}</span>
                    </div>
                    {selectedType === 'video_call' && (
                      <div className="flex justify-between text-sm pt-2 border-t border-slate-200">
                        <span className="text-slate-500">Statut paiement</span>
                        <Badge className="bg-emerald-100 text-emerald-700">Payé ✓</Badge>
                      </div>
                    )}
                  </div>
                  {selectedType === 'video_call' && (
                    <div className="bg-blue-50 rounded-lg p-3 mb-4 text-left">
                      <p className="text-xs text-blue-800">
                        <strong>📹 Téléconsultation :</strong> Vous recevrez le lien de la visioconférence par notification avant votre rendez-vous.
                      </p>
                    </div>
                  )}
                  <Button
                    onClick={() => navigate('/appointments')}
                    className="w-full bg-red-500 hover:bg-red-600 rounded-full"
                  >
                    Voir mes rendez-vous
                  </Button>
                </div>
              ) : paymentStatus === 'failed' ? (
                // Paiement échoué
                <div className="text-center py-6">
                  <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                    <AlertCircle className="w-10 h-10 text-red-600" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 mb-2">Paiement échoué</h2>
                  <p className="text-slate-600 mb-6">
                    Le paiement n&apos;a pas pu être traité. Veuillez réessayer.
                  </p>
                  <div className="space-y-3">
                    <Button
                      onClick={() => {
                        setBookingStep(3);
                        setPaymentStatus('pending');
                        setPaymentReference(null);
                      }}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      Réessayer le paiement
                    </Button>
                    <Button
                      onClick={() => navigate('/')}
                      variant="ghost"
                      className="w-full text-slate-500"
                    >
                      Retour à l&apos;accueil
                    </Button>
                  </div>
                </div>
              ) : (
                // Téléconsultation - En attente de confirmation paiement automatique
                <div className="text-center py-6">
                  <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-6 relative">
                    <Smartphone className="w-10 h-10 text-purple-600" />
                    {pollingInterval && (
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center">
                        <RefreshCw className="w-3 h-3 text-white animate-spin" />
                      </div>
                    )}
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 mb-2">
                    {pollingInterval ? 'Vérification du paiement...' : 'Paiement en cours...'}
                  </h2>
                  <p className="text-slate-600 mb-6">
                    {pollingInterval 
                      ? 'Nous vérifions automatiquement votre paiement. Cette page se mettra à jour automatiquement.'
                      : 'Finalisez votre paiement sur Chariow pour confirmer votre téléconsultation.'
                    }
                  </p>
                  
                  {/* Reference badge */}
                  {paymentReference && (
                    <div className="mb-4">
                      <Badge variant="outline" className="text-purple-600 border-purple-300">
                        Référence : {paymentReference}
                      </Badge>
                    </div>
                  )}
                  
                  <div className="p-4 bg-purple-50 rounded-xl text-left mb-6 border border-purple-200">
                    <p className="text-sm font-medium text-purple-900 mb-3">📋 Récapitulatif :</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-purple-700">Professionnel</span>
                        <span className="font-medium text-purple-900">Dr. {professional.user?.full_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-purple-700">Date</span>
                        <span className="font-medium text-purple-900">{selectedDate && format(selectedDate, 'dd MMMM yyyy', { locale: fr })}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-purple-700">Heure</span>
                        <span className="font-medium text-purple-900">{selectedTime}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-purple-200">
                        <span className="text-purple-700">Montant</span>
                        <span className="font-bold text-purple-900">{getPrice().toLocaleString()} FCFA</span>
                      </div>
                    </div>
                  </div>

                  {/* Polling indicator */}
                  {pollingInterval && (
                    <div className="bg-blue-50 rounded-lg p-3 mb-4 text-left flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 text-blue-600 animate-spin" />
                      <p className="text-xs text-blue-800">
                        Vérification automatique en cours... La page se mettra à jour dès réception du paiement.
                      </p>
                    </div>
                  )}

                  <div className="space-y-3">
                    <Button
                      onClick={async () => {
                        // Manually check status
                        if (paymentReference) {
                          try {
                            const response = await chariowAPI.checkStatus(paymentReference);
                            if (response.data.status === 'confirmed') {
                              setPaymentStatus('confirmed');
                              setBookingSuccess(true);
                              setCreatedAppointment({ id: response.data.appointment_id });
                              toast.success('Paiement confirmé !');
                            } else {
                              toast.info('Paiement non encore reçu. Veuillez patienter.');
                            }
                          } catch (error) {
                            toast.error('Erreur lors de la vérification');
                          }
                        }
                      }}
                      variant="outline"
                      className="w-full"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Vérifier manuellement
                    </Button>
                    <Button
                      onClick={() => {
                        const price = getPrice();
                        const { tier } = getChariowLink(price);
                        const url = paymentReference 
                          ? `https://urvjccdj.mychariow.store/prd_${tier === 3000 ? 'f8mu6p3a' : tier === 5000 ? 'tm5xy3d5' : tier === 10000 ? 'zh8ll8sw' : 'cb8048jl'}?ref=${paymentReference}`
                          : getChariowLink(price).link;
                        window.open(url, '_blank');
                      }}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Ouvrir Chariow à nouveau
                    </Button>
                    <Button
                      onClick={() => navigate('/')}
                      variant="ghost"
                      className="w-full text-slate-500"
                    >
                      Retour à l&apos;accueil
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfessionalDetail;
